import { postJson, request } from './request';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  hasPassword: boolean;
  hasGoogle: boolean;
  plan: 'free' | 'basic' | 'premium';
  hasBilling: boolean;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  subscriptionEndsAt: string | null;
  totpEnabled: boolean;
  mfaPromptDismissed: boolean;
  createdAt: string;
}

/** Password sign-in either completes immediately or requires a TOTP code. */
export type LoginResult =
  | { user: AuthUser; mfaRequired?: undefined }
  | { mfaRequired: true; mfaToken: string; user?: undefined };

export const fetchAuthConfig = (): Promise<{ googleClientId: string | null }> =>
  request('/api/auth/config');

export const fetchCurrentUser = async (): Promise<AuthUser | null> => {
  const data = await request<{ user: AuthUser | null }>('/api/auth/me');
  return data.user;
};

export const register = async (input: {
  email: string;
  name: string;
  password: string;
}): Promise<AuthUser> => {
  const data = await postJson<{ user: AuthUser }>('/api/auth/register', input);
  return data.user;
};

export const login = (input: { email: string; password: string }): Promise<LoginResult> =>
  postJson<LoginResult>('/api/auth/login', input);

export const verifyMfaLogin = async (input: {
  mfaToken: string;
  code: string;
}): Promise<AuthUser> => {
  const data = await postJson<{ user: AuthUser }>('/api/auth/mfa/verify-login', input);
  return data.user;
};

export interface MfaSetup {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

/** Starts 2FA enrollment; returns the QR code + secret to show the user. */
export const setupMfa = (): Promise<MfaSetup> => postJson<MfaSetup>('/api/auth/mfa/setup', {});

export const enableMfa = async (code: string): Promise<AuthUser> => {
  const data = await postJson<{ user: AuthUser }>('/api/auth/mfa/enable', { code });
  return data.user;
};

export const disableMfa = async (code: string): Promise<AuthUser> => {
  const data = await postJson<{ user: AuthUser }>('/api/auth/mfa/disable', { code });
  return data.user;
};

/** Marks the 2FA onboarding prompt as skipped so it isn't shown on future sign-ins. */
export const dismissMfaPrompt = async (): Promise<AuthUser> => {
  const data = await postJson<{ user: AuthUser }>('/api/auth/mfa/dismiss', {});
  return data.user;
};

export const loginWithGoogle = async (credential: string): Promise<AuthUser> => {
  const data = await postJson<{ user: AuthUser }>('/api/auth/google', { credential });
  return data.user;
};

export const logout = (): Promise<void> =>
  request<void>('/api/auth/logout', { method: 'POST' });

/** Dev-only instant upgrade when Stripe is not configured on the server. */
export const upgradePlan = async (plan: AuthUser['plan']): Promise<AuthUser> => {
  const data = await postJson<{ user: AuthUser }>('/api/auth/plan', { plan });
  return data.user;
};

export const updateProfile = async (input: {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
}): Promise<AuthUser> => {
  const data = await request<{ user: AuthUser }>('/api/auth/profile', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return data.user;
};

export const deleteAccount = async (input: {
  password?: string;
  confirmation?: string;
}): Promise<void> =>
  request<void>('/api/auth/account', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

export const requestPasswordReset = async (input: { email: string }): Promise<void> => {
  await postJson<{ message: string }>('/api/auth/forgot-password', input);
};

export const resetPassword = async (input: {
  token: string;
  newPassword: string;
}): Promise<AuthUser> => {
  const data = await postJson<{ user: AuthUser }>('/api/auth/reset-password', input);
  return data.user;
};
